import { watch } from 'vue';
import type { Router } from 'vue-router';
import type { Ref } from 'vue';
import type { FeatureNode } from '@fondationcss/sdk';
import { resolveFeatureRedirect } from '@/utils/group-training-feature';

/** The Fondation's feature-administration screen (Gestion › Système › Fonctionnalités). */
export const FEATURES_ADMIN_PATH = '/core/features';

/**
 * True when a navigation LEAVES the feature-administration screen: the moment to reload the
 * flags. The Fondation's screen writes the store but leaves the boot-time `Features.flags`
 * ref stale, so without this the menus, the toolbar and the guard only see a toggle after a
 * full page reload (#812 acceptance).
 */
export function leftFeaturesAdmin(fromPath: string, toPath: string): boolean {
  return fromPath.startsWith(FEATURES_ADMIN_PATH) && !toPath.startsWith(FEATURES_ADMIN_PATH);
}

/** The flag map the catalog tree carries — `effective` is the value that actually gates. */
export function flagsFromCatalog(nodes: FeatureNode[]): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  const walk = (level: FeatureNode[]) => {
    for (const node of level) {
      flags[node.key] = node.effective;
      walk(node.children ?? []);
    }
  };
  walk(nodes);
  return flags;
}

/**
 * Routes whose `meta.feature` flag is off redirect to their section's dashboard (#812) — the
 * menu ACL hides the entries, this covers deep links, bookmarks and in-app links from screens
 * that survive the flag. Same installation pattern as `installOfflineRedirect` (main.ts).
 *
 * Two triggers, both needed: the `beforeEach` rules on every navigation, and the watch handles
 * the boot race — flags load during `sdk.initialize()`, so a deep link can land on a gated
 * page BEFORE the flags say it is withdrawn; when they arrive, the standing route is re-judged.
 */
export function installFeatureRouteGuard(
  router: Router,
  features: {
    flags: Ref<Record<string, boolean>>;
    getCatalog: () => Promise<FeatureNode[]>;
  }
): void {
  router.beforeEach((to) => {
    const target = resolveFeatureRedirect(to, features.flags.value);
    return target ? { path: target } : true;
  });

  watch(features.flags, (flags) => {
    const target = resolveFeatureRedirect(router.currentRoute.value, flags);
    if (target) void router.replace({ path: target });
  }, { deep: true });

  // Leaving the administration screen refreshes the flags, so a toggle takes effect on the
  // very next navigation instead of the next full page load. NOT `Features.load()`: the SDK
  // memoizes it (one fetch per boot, by design), so it hands back the stale boot promise.
  // The catalog endpoint is fetched instead and flattened into the reactive ref — the menu
  // ACL, the toolbar axes and the guard's watch above all read that ref. Permission is a
  // given here: this only ever fires for someone who could open /core/features.
  router.afterEach((to, from) => {
    if (!leftFeaturesAdmin(from.path, to.path)) return;
    void features.getCatalog()
      .then(catalog => { features.flags.value = flagsFromCatalog(catalog); })
      .catch(() => { /* stale flags until the next full load — same as before this hook */ });
  });
}
